import { ReportExportButtons } from '../components/ReportExportButtons'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  executiveApi,
  isOperationalMetric,
  type ExecutiveBucket,
  type ExecutiveCell,
  type ExecutiveDrill,
  type ExecutiveDrillPage,
  type ExecutiveFilters,
  type ExecutiveOperationalPage,
  type ExecutiveRankRow,
  type ExecutiveReport,
} from '../api/executive'
import { fetchDepartments, fetchOperators, fetchProducts, type Department, type Operator } from '../api/catalog'
import { contestsApi } from '../api/performance'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import { KpiAnomaliesPanel } from '../components/KpiAnomaliesPanel'
import { KpiEstesiPanel } from '../components/KpiEstesiPanel'
import { RevenueCommissionPanel } from '../components/RevenueCommissionPanel'
import { EconomicCockpitPanel } from '../components/EconomicCockpitPanel'
import {
  CardGrid, StatGrid, Button, Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, FilterBar, LinearProgress,
  LoadingState, Page, PageHeader, Select, StatusPill, TextField, List, ListItem, StatCard,
} from '../components/ui'

/**
 * F-065 (TASK-0319) — cockpit Direzione: una schermata per produzione, classifiche, andamento, confronto col
 * periodo precedente, pipeline, qualità, cockpit operativo e backoffice, tutta filtrabile coi filtri globali e
 * cliccabile fino alle pratiche. NESSUN importo: né fatturato, provvigioni, crediti, margini o previsioni.
 * La pagina non fa gating di ruolo: il gate è il Manager del backend, chi non è autorizzato vede l'errore.
 */

const STATUS_LABELS: Record<string, string> = {
  Bozza: 'Bozza',
  InAttesaDiFirma: 'In attesa di firma',
  InviataAlBackoffice: 'Inviata al backoffice',
  DaVerificare: 'Da verificare',
  IntegrazioneRichiesta: 'Integrazione richiesta',
  Verificata: 'Verificata',
  InAttesaDiAssegnazioneScarico: 'In attesa di lotto',
  AssegnataALotto: 'Assegnata a lotto',
  ApprovazioneRichiesta: 'Approvazione richiesta',
  Approvata: 'Approvata',
  CaricamentoOperatoreInCorso: 'Caricamento operatore in corso',
  BozzaOperatoreCreata: 'Bozza operatore creata',
  DaConfermare: 'Da confermare',
  InviataAllOperatore: "Inviata all'operatore",
  InLavorazioneOperatore: 'In lavorazione operatore',
  Attivata: 'Attivata',
  Rifiutata: 'Rifiutata',
  Annullata: 'Annullata',
  Sospesa: 'Sospesa',
  Stornata: 'Stornata',
}

const EXCEPTION_LABELS: Record<string, string> = {
  CampoMancante: 'Campo mancante',
  DocumentoMancante: 'Documento mancante',
  DocumentoScaduto: 'Documento scaduto',
  IbanNonValido: 'IBAN non valido',
  IncongruenzaDati: 'Incongruenza dati',
  Duplicato: 'Duplicato',
  ProvvigioneAnomala: 'Provvigione anomala',
}

const QUALITY_LABELS: Record<string, string> = {
  rejectRate: 'Tasso di rifiuto (rifiutate / inviate)',
  cancellationRate: 'Tasso di annullamento (annullate / pratiche)',
  integrationRate: 'Tasso di integrazione (integrazioni / pratiche)',
  activationRateVsSubmitted: 'Tasso di attivazione sulle inviate (attivate / inviate)',
  activationRateOnContracts: 'Attivazione su contratti (attivate / inserite)',
}

const MEASURE_LABELS: Record<string, string> = {
  inserted: 'Inserite',
  submitted: 'Inviate',
  activated: 'Attivate',
  reversed: 'Storni',
}

const it = (n: number) => n.toLocaleString('it-IT')
const pct = (v: number | null) =>
  v === null ? '—' : `${(v * 100).toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
const signed = (n: number) => `${n > 0 ? '+' : ''}${it(n)}`
const dayLabel = (iso: string) => {
  const [, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}`
}

interface DrillState {
  drill: ExecutiveDrill
  title: string
}

/** Un numero cliccabile: apre il drill-down se la cella ne ha uno. */
function Num({ cell, title, onDrill }: { cell: ExecutiveCell; title: string; onDrill: (d: DrillState) => void }) {
  if (cell.count === null) return <span className="text-muted">n.d.</span>
  if (!cell.drill || cell.count === 0) return <span>{it(cell.count)}</span>
  const drill = cell.drill
  return (
    <Button variant="text" size="sm" onClick={() => onDrill({ drill, title })}>
      {it(cell.count)}
    </Button>
  )
}

function Tile({ label, cell, onDrill, hint }: { label: string; cell: ExecutiveCell; onDrill: (d: DrillState) => void; hint?: string }) {
  return (
    <StatCard
      label={label}
      value={<Num cell={cell} title={label} onDrill={onDrill} />}
      supporting={hint}
    />
  )
}

function BucketTable({
  title, rows, onDrill, empty,
}: { title: string; rows: ExecutiveBucket[]; onDrill: (d: DrillState) => void; empty: string }) {
  const max = Math.max(1, ...rows.map(r => r.inserted.count ?? 0))
  return (
    <Card padding="none">
      <CardHeader title={title} />
      {rows.length === 0 ? (
        <CardContent className="text-muted md-typescale-body-small">{empty}</CardContent>
      ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Voce</th>
                <th className="text-end">Inserite</th>
                <th className="text-end">Inviate</th>
                <th className="text-end">Attivate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.key}>
                  <td className="md-u-minw-140">
                    <div>{r.label}</div>
                    <LinearProgress value={((r.inserted.count ?? 0) / max) * 100} aria-label={`Inserite: ${r.label}`} />
                  </td>
                  <td className="text-end md-num"><Num cell={r.inserted} title={`${title}: ${r.label} (inserite)`} onDrill={onDrill} /></td>
                  <td className="text-end md-num"><Num cell={r.submitted} title={`${title}: ${r.label} (inviate)`} onDrill={onDrill} /></td>
                  <td className="text-end md-num"><Num cell={r.activated} title={`${title}: ${r.label} (attivate)`} onDrill={onDrill} /></td>
                </tr>
              ))}
            </tbody>
          </DataTable>
      )}
    </Card>
  )
}

function RankTable({ title, rows, onDrill }: { title: string; rows: ExecutiveRankRow[]; onDrill: (d: DrillState) => void }) {
  return (
    <Card padding="none">
      <CardHeader title={title} />
      {rows.length === 0 ? (
        <CardContent className="text-muted md-typescale-body-small">Nessuna attivazione o pratica inserita nel periodo.</CardContent>
      ) : (
          <DataTable variant="plain">
            <thead>
              <tr><th>#</th><th>Voce</th><th className="text-end">Attivate</th><th className="text-end">Inserite</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key}>
                  <td>{i + 1}</td>
                  <td>{r.label}</td>
                  <td className="text-end md-num"><Num cell={r.activated} title={`${r.label} (attivate)`} onDrill={onDrill} /></td>
                  <td className="text-end md-num"><Num cell={r.inserted} title={`${r.label} (inserite)`} onDrill={onDrill} /></td>
                </tr>
              ))}
            </tbody>
          </DataTable>
      )}
    </Card>
  )
}

export function ExecutiveCockpitPage() {
  const [form, setForm] = useState<ExecutiveFilters>({})
  const [applied, setApplied] = useState<ExecutiveFilters>({})
  const [report, setReport] = useState<ExecutiveReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [departments, setDepartments] = useState<Department[]>([])
  const [operators, setOperators] = useState<Operator[]>([])
  const [products, setProducts] = useState<{ id: number; name: string }[]>([])
  const [users, setUsers] = useState<DirectoryUser[] | null>(null)
  const [contests, setContests] = useState<{ id: number; name: string }[]>([])

  const [drill, setDrill] = useState<DrillState | null>(null)
  const [drillPage, setDrillPage] = useState(1)
  const [drillData, setDrillData] = useState<ExecutiveDrillPage | null>(null)
  // F-141 (REQ-037-003): lead, appuntamenti, task, gare e anomalie di durata hanno un elenco proprio.
  const [opData, setOpData] = useState<ExecutiveOperationalPage | null>(null)
  const [drillLoading, setDrillLoading] = useState(false)
  const [drillError, setDrillError] = useState('')

  const load = useCallback(async (f: ExecutiveFilters) => {
    setLoading(true)
    setError('')
    setDrill(null)
    try {
      setReport(await executiveApi.get(f))
      setApplied(f)
    } catch (e) {
      setReport(null)
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load({})
    // Elenchi per i filtri: ognuno può mancare (permessi) senza bloccare il cockpit.
    void fetchDepartments().then(setDepartments).catch(() => undefined)
    void fetchOperators().then(r => setOperators(r.items)).catch(() => undefined)
    void fetchProducts({ pageSize: 200 }).then(r => setProducts(r.items)).catch(() => undefined)
    void userDirectoryApi.list({ limit: 200 }).then(setUsers).catch(() => setUsers(null))
    void contestsApi.list().then(r => setContests(r.items)).catch(() => undefined)
    // Caricamento iniziale: i filtri successivi si applicano col pulsante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!drill) {
      setDrillData(null)
      setOpData(null)
      return
    }
    let cancelled = false
    setDrillLoading(true)
    setDrillError('')
    const operational = isOperationalMetric(drill.drill.metric)
    const request = operational
      ? executiveApi.drilldownOperational(applied, drill.drill, drillPage).then(d => { if (!cancelled) { setOpData(d); setDrillData(null) } })
      : executiveApi.drilldown(applied, drill.drill, drillPage).then(d => { if (!cancelled) { setDrillData(d); setOpData(null) } })
    request
      .catch(e => {
        if (!cancelled) { setDrillData(null); setOpData(null); setDrillError(e instanceof Error ? e.message : 'Errore') }
      })
      .finally(() => { if (!cancelled) setDrillLoading(false) })
    return () => { cancelled = true }
  }, [drill, drillPage, applied])

  const openDrill = (d: DrillState) => {
    setDrillPage(1)
    setDrill(d)
  }

  const set = (k: keyof ExecutiveFilters) => (e: { target: { value: string } }) =>
    setForm(f => ({ ...f, [k]: e.target.value || undefined }))

  const userField = (k: 'agentId' | 'teamOf' | 'areaManagerId', label: string, role?: string) =>
    users ? (
      <Select label={label} value={form[k] ?? ''} onChange={set(k)}>
        <option value="">Tutti</option>
        {users.filter(u => !role || u.role === role).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
      </Select>
    ) : (
      <TextField label={label} type="number" min={1} className="md-u-w-112" value={form[k] ?? ''} onChange={set(k)} placeholder="ID utente" />
    )

  const maxDaily = report ? Math.max(1, ...report.daily.map(d => Math.max(d.inserted.count ?? 0, d.activated.count ?? 0))) : 1
  const maxPipeline = report ? Math.max(1, ...report.pipeline.byStatus.map(s => s.count.count ?? 0)) : 1
  const maxBacklog = report ? Math.max(1, ...report.backoffice.backlog.byStatus.map(s => s.count.count ?? 0)) : 1

  return (
    <Page>
      <PageHeader
        title="Cockpit direzione"
        icon="monitoring"
        {...(report ? { subtitle: `${report.from.slice(0, 10)} - ${report.to.slice(0, 10)} (Europe/Rome)` } : {})}
        actions={
          <>
            <ReportExportButtons title="Cockpit Direzione" />
            {report?.dataSource === 'aggregate' && report.computedAt && (
              <StatusPill tone="neutral" title="Produzione, classifiche, andamento e confronto sono letti dall'aggregato giornaliero; il resto e' calcolato dal vivo.">
                dati aggregati delle {new Date(report.computedAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' })}
              </StatusPill>
            )}
          </>
        }
      />

      <FilterBar onSubmit={() => void load(form)} onReset={() => { setForm({}); void load({}) }}>
        <TextField label="Da" type="date" value={form.from ?? ''} onChange={set('from')} />
        <TextField label="A" type="date" value={form.to ?? ''} onChange={set('to')} />
        {userField('agentId', 'Agente')}
        {userField('teamOf', 'Team di')}
        {userField('areaManagerId', 'Area manager', 'EqualAreaManager')}
        <Select label="Settore" value={form.departmentId ?? ''} onChange={set('departmentId')}>
          <option value="">Tutti</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Select>
        <Select label="Operatore" value={form.operatorId ?? ''} onChange={set('operatorId')}>
          <option value="">Tutti</option>
          {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Select>
        <Select label="Prodotto" value={form.productId ?? ''} onChange={set('productId')}>
          <option value="">Tutti</option>
          {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select label="Stato pratica" value={form.status ?? ''} onChange={set('status')}>
          <option value="">Tutti</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
        <Select label="Gara" value={form.contestId ?? ''} onChange={set('contestId')}>
          <option value="">Nessuna</option>
          {contests.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select label="Tipo cliente" value={form.customerType ?? ''} onChange={set('customerType')}>
          <option value="">Tutti</option>
          <option value="consumer">Consumer</option>
          <option value="business">Business</option>
        </Select>
      </FilterBar>
      <div className="text-muted md-typescale-body-small">Senza date: ultimi 30 giorni. Massimo 366 giorni. Gli importi compaiono solo nei KPI estesi, per chi ha la lettura amministrativa.</div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <Callout tone="warning">Cockpit non disponibile. {error}</Callout>
      ) : report ? (
        <>
          {/* F-082: prima le anomalie (§273), con gli stessi filtri già applicati al cockpit. */}
          <KpiAnomaliesPanel filters={applied} onDrill={openDrill} />

          <KpiEstesiPanel filters={applied} />
          <EconomicCockpitPanel filters={applied} />
          <RevenueCommissionPanel filters={applied} />

          {report.snapshot.migrated > 0 && (
            <Callout tone="info">
              {it(report.snapshot.migrated)} pratiche su {it(report.snapshot.contractsInScope)} arrivano dal vecchio sistema
              (di cui {it(report.snapshot.migratedSnapshotOnly)} con la sola fotografia dello stato): contano in pipeline e backlog,
              ma non fra inserite, inviate o attivate del periodo, perché non hanno eventi reali.
            </Callout>
          )}

          <StatGrid>
            <Tile label="Inserite" cell={report.production.inserted} onDrill={openDrill} hint="Pratiche native create nel periodo" />
            <Tile label="Inviate" cell={report.production.submitted} onDrill={openDrill} hint="Primo invio al backoffice nel periodo" />
            <Tile label="Attivate" cell={report.production.activated} onDrill={openDrill} hint="Prima attivazione nel periodo" />
            <Tile label="Storni" cell={report.production.reversed} onDrill={openDrill} hint="Transizioni a Stornata nel periodo" />
          </StatGrid>

          <Card padding="none">
            <CardHeader
              title="Confronto col periodo precedente"
              subtitle={`${report.comparison.previousFrom.slice(0, 10)} - ${report.comparison.previousTo.slice(0, 10)}`}
            />
              <DataTable variant="plain">
                <thead>
                  <tr><th>Misura</th><th className="text-end">Periodo</th><th className="text-end">Precedente</th><th className="text-end">Differenza</th><th className="text-end">%</th></tr>
                </thead>
                <tbody>
                  {report.comparison.metrics.map(m => (
                    <tr key={m.measure}>
                      <td>{MEASURE_LABELS[m.measure] ?? m.measure}</td>
                      <td className="text-end md-num">{it(m.current)}</td>
                      <td className="text-end md-num">{it(m.previous)}</td>
                      <td className="text-end md-num">{signed(m.delta)}</td>
                      <td className="text-end md-num">{m.deltaPct === null ? '—' : `${m.deltaPct > 0 ? '+' : ''}${pct(m.deltaPct)}`}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </Card>

          <Card>
            <CardHeader title="Andamento giornaliero" subtitle="inserite (blu) e attivate (verde)" />
            <CardContent>
              <div className="d-flex align-items-end gap-1 md-u-h-120 md-u-scroll-x">
                {report.daily.map(d => (
                  <div key={d.day} className="text-center md-u-minw-28">
                    <div className="d-flex align-items-end justify-content-center gap-1 md-u-h-88">
                      <div
                        title={`${d.day.slice(0, 10)} inserite: ${d.inserted.count ?? 0}`}
                        style={{ width: 9, height: `${((d.inserted.count ?? 0) / maxDaily) * 100}%`, minHeight: d.inserted.count ? 2 : 0, background: 'hsl(var(--md-primary))', cursor: d.inserted.drill && d.inserted.count ? 'pointer' : 'default' }}
                        onClick={() => d.inserted.drill && d.inserted.count && openDrill({ drill: d.inserted.drill, title: `Inserite il ${dayLabel(d.day)}` })}
                      />
                      <div
                        title={`${d.day.slice(0, 10)} attivate: ${d.activated.count ?? 0}`}
                        style={{ width: 9, height: `${((d.activated.count ?? 0) / maxDaily) * 100}%`, minHeight: d.activated.count ? 2 : 0, background: 'hsl(var(--md-success))', cursor: d.activated.drill && d.activated.count ? 'pointer' : 'default' }}
                        onClick={() => d.activated.drill && d.activated.count && openDrill({ drill: d.activated.drill, title: `Attivate il ${dayLabel(d.day)}` })}
                      />
                    </div>
                    <div className="text-muted md-typescale-label-small">{dayLabel(d.day)}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <CardGrid min={360}>
            <BucketTable title="Produzione per settore" rows={report.production.byDepartment} onDrill={openDrill} empty="Nessuna produzione nel periodo." />
            <BucketTable title="Produzione per operatore" rows={report.production.byOperator} onDrill={openDrill} empty="Nessuna produzione nel periodo." />
            <BucketTable title="Produzione per team" rows={report.production.byTeam} onDrill={openDrill} empty="Nessuna produzione nel periodo." />
            <BucketTable title="Produzione per prodotto" rows={report.production.byProduct} onDrill={openDrill} empty="Nessuna produzione nel periodo." />
            <RankTable title="Top 10 agenti" rows={report.topAgents} onDrill={openDrill} />
            <RankTable title="Top 10 prodotti" rows={report.topProducts} onDrill={openDrill} />
          </CardGrid>

          <CardGrid min={360}>
              <Card>
                <CardHeader title="Pipeline aperta" actions={<Num cell={report.pipeline.total} title="Pipeline aperta" onDrill={openDrill} />} />
                <CardContent>
                  <div className="md-typescale-body-small text-muted mb-2">Fotografia dello stato attuale, non dipende dal periodo.</div>
                  {report.pipeline.byStatus.length === 0 && <div className="text-muted md-typescale-body-small">Nessuna pratica aperta.</div>}
                  {report.pipeline.byStatus.map(s => (
                    <div key={s.status} className="mb-2">
                      <div className="d-flex justify-content-between md-typescale-body-small">
                        <span>{STATUS_LABELS[s.status] ?? s.status}</span>
                        <Num cell={s.count} title={`Pipeline: ${STATUS_LABELS[s.status] ?? s.status}`} onDrill={openDrill} />
                      </div>
                      <LinearProgress value={((s.count.count ?? 0) / maxPipeline) * 100} aria-label={`Pipeline: ${STATUS_LABELS[s.status] ?? s.status}`} />
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card padding="none">
                <CardHeader title="Qualità" />
                  <DataTable variant="plain">
                    <thead><tr><th>Indicatore</th><th className="text-end">Tasso</th><th className="text-end">Numeratore</th><th className="text-end">Denominatore</th></tr></thead>
                    <tbody>
                      {report.quality.map(q => (
                        <tr key={q.name}>
                          <td>{QUALITY_LABELS[q.name] ?? q.name}</td>
                          <td className="text-end md-num">{pct(q.rate)}</td>
                          <td className="text-end md-num"><Num cell={q.numerator} title={`${QUALITY_LABELS[q.name] ?? q.name}: numeratore`} onDrill={openDrill} /></td>
                          <td className="text-end md-num"><Num cell={q.denominator} title={`${QUALITY_LABELS[q.name] ?? q.name}: denominatore`} onDrill={openDrill} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
              </Card>
          </CardGrid>

          <Card>
            <CardHeader title="Cockpit operativo" />
            <CardContent>
              {!report.operational.applicable && (
                <Callout tone="info">
                  Con un filtro per operatore, prodotto, stato o tipo cliente lead, appuntamenti e task non sono calcolabili.
                </Callout>
              )}
              <StatGrid>
                <Tile label="Lead nuovi" cell={report.operational.newLeads} onDrill={openDrill} />
                <Tile label="Appuntamenti effettuati" cell={report.operational.appointmentsCompleted} onDrill={openDrill} />
                <Tile label="Appuntamenti no show" cell={report.operational.appointmentsNoShow} onDrill={openDrill} />
                <Tile label="Task scaduti aperti" cell={report.operational.tasksOverdue} onDrill={openDrill} hint="Alla data di oggi" />
              </StatGrid>
              <div className="mt-3">
                <strong className="md-typescale-body-small">Gare attive</strong>
                {report.operational.activeContests.length === 0 ? (
                  <div className="text-muted md-typescale-body-small">Nessuna gara attiva.</div>
                ) : (
                  <List aria-label="Gare attive">
                    {report.operational.activeContests.map(c => (
                      <ListItem
                        key={c.id}
                        headline={<><Link to={`/contests/${c.id}`}>{c.name}</Link> <span className="text-muted md-typescale-body-small">{c.startDate.slice(0, 10)} - {c.endDate.slice(0, 10)}</span></>}
                        trailing={
                          <span className="md-typescale-body-small">
                            {c.participants > 0 ? (
                              <Button variant="text" size="sm" onClick={() => openDrill({ drill: c.drill, title: `Partecipanti: ${c.name}` })}>
                                {it(c.participants)}
                              </Button>
                            ) : it(c.participants)} partecipanti
                          </span>
                        }
                      />
                    ))}
                  </List>
                )}
              </div>
            </CardContent>
          </Card>

          <CardGrid min={360}>
              <Card>
                <CardHeader title="Backlog backoffice" actions={<Num cell={report.backoffice.backlog.total} title="Backlog backoffice" onDrill={openDrill} />} />
                <CardContent>
                  {report.backoffice.backlog.byStatus.map(s => (
                    <div key={s.status} className="mb-2">
                      <div className="d-flex justify-content-between md-typescale-body-small">
                        <span>{STATUS_LABELS[s.status] ?? s.status}</span>
                        <Num cell={s.count} title={`Backlog: ${STATUS_LABELS[s.status] ?? s.status}`} onDrill={openDrill} />
                      </div>
                      <LinearProgress value={((s.count.count ?? 0) / maxBacklog) * 100} aria-label={`Backlog: ${STATUS_LABELS[s.status] ?? s.status}`} />
                    </div>
                  ))}
                  <div className="mt-3 md-typescale-body-small">
                    Fuori SLA: <strong><Num cell={report.backoffice.slaBreached} title="Pratiche fuori SLA" onDrill={openDrill} /></strong>
                    {' '}su {it(report.backoffice.slaEvaluated)} con SLA
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader title="Eccezioni aperte" actions={<Num cell={report.backoffice.exceptions.total} title="Pratiche con eccezioni" onDrill={openDrill} />} />
                <CardContent>
                  {report.backoffice.exceptions.byType.length === 0 && <div className="text-muted md-typescale-body-small">Nessuna eccezione aperta.</div>}
                  <List aria-label="Eccezioni aperte per tipo">
                    {report.backoffice.exceptions.byType.map(t => (
                      <ListItem
                        key={t.type}
                        headline={EXCEPTION_LABELS[t.type] ?? t.type}
                        trailing={<Num cell={t.count} title={`Eccezioni: ${EXCEPTION_LABELS[t.type] ?? t.type}`} onDrill={openDrill} />}
                      />
                    ))}
                  </List>
                </CardContent>
              </Card>
          </CardGrid>

          {drill && (
            <Card padding="none" id="executive-drill">
              <CardHeader title={drill.title} actions={<Button size="sm" variant="text" onClick={() => setDrill(null)}>Chiudi</Button>} />
              {drillLoading ? (
                <LoadingState />
              ) : drillError ? (
                <CardContent className="md-typescale-body-small text-danger">{drillError}</CardContent>
              ) : opData ? (
                <>
                    <DataTable variant="plain">
                      <thead>
                        <tr><th>{opData.metric === 'contest' ? 'Gara' : 'Titolo'}</th><th>{opData.metric === 'contest' ? 'Partecipante' : 'Assegnato a'}</th><th>Stato</th><th>{opData.metric === 'contest' ? 'Posizione' : 'Data'}</th></tr>
                      </thead>
                      <tbody>
                        {opData.items.map(i => (
                          <tr key={`${i.kind}-${i.id}`}>
                            <td><Link to={i.link}>{i.title}</Link></td>
                            <td>{i.userName ?? '—'}</td>
                            <td>{i.status ? (STATUS_LABELS[i.status] ?? i.status) : '—'}</td>
                            <td>
                              {opData.metric === 'contest'
                                ? (i.position ?? '—')
                                : i.date ? new Date(i.date).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' }) : '—'}
                            </td>
                          </tr>
                        ))}
                        {opData.items.length === 0 && <DataTableEmptyRow colSpan={4} title="Nessuna riga." />}
                      </tbody>
                    </DataTable>
                  <div className="d-flex justify-content-between align-items-center md-typescale-body-small p-3">
                    <span>{it(opData.total)} righe</span>
                    <span>
                      <Button size="sm" variant="outlined" className="me-2" disabled={drillPage <= 1} onClick={() => setDrillPage(p => p - 1)}>Precedenti</Button>
                      pagina {opData.page} di {Math.max(1, Math.ceil(opData.total / opData.pageSize))}
                      <Button size="sm" variant="outlined" className="ms-2" disabled={drillPage * opData.pageSize >= opData.total} onClick={() => setDrillPage(p => p + 1)}>Successive</Button>
                    </span>
                  </div>
                </>
              ) : drillData ? (
                <>
                    <DataTable variant="plain">
                      <thead><tr><th>Riferimento</th><th>Agente</th><th>Settore</th><th>Stato</th><th>Data</th></tr></thead>
                      <tbody>
                        {drillData.items.map(i => (
                          <tr key={i.id}>
                            <td><Link to={`/contracts/${i.id}`}>{i.referenceCode}</Link></td>
                            <td>{i.agentName ?? '—'}</td>
                            <td>{i.departmentName ?? '—'}</td>
                            <td>{STATUS_LABELS[i.status] ?? i.status}</td>
                            <td>{new Date(i.createdAt).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })}</td>
                          </tr>
                        ))}
                        {drillData.items.length === 0 && <DataTableEmptyRow colSpan={5} title="Nessuna pratica." />}
                      </tbody>
                    </DataTable>
                  <div className="d-flex justify-content-between align-items-center md-typescale-body-small p-3">
                    <span>{it(drillData.total)} pratiche</span>
                    <span>
                      <Button size="sm" variant="outlined" className="me-2" disabled={drillPage <= 1} onClick={() => setDrillPage(p => p - 1)}>Precedenti</Button>
                      pagina {drillData.page} di {Math.max(1, Math.ceil(drillData.total / drillData.pageSize))}
                      <Button size="sm" variant="outlined" className="ms-2" disabled={drillPage * drillData.pageSize >= drillData.total} onClick={() => setDrillPage(p => p + 1)}>Successive</Button>
                    </span>
                  </div>
                </>
              ) : null}
            </Card>
          )}

          <ul className="md-typescale-body-small text-muted">
            {report.limitations.map(l => <li key={l}>{l}</li>)}
          </ul>
        </>
      ) : null}
    </Page>
  )
}
